import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Modal, ConfirmModal } from '../../components/ui/Modal';
import {
  listAdminShops,
  updateShopStatus,
  manualRenewShop,
  type AdminShop,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import { Search, Building2, Users, Phone, RefreshCw, Settings2 } from 'lucide-react';

const statusLabels: Record<string, string> = {
  trial: '试用中',
  active: '正式使用',
  readonly: '只读期',
  expired: '已过期',
};

const statusVariants: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'danger'> = {
  trial: 'primary',
  active: 'success',
  readonly: 'warning',
  expired: 'danger',
};

export function ShopsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const [statusTarget, setStatusTarget] = useState<AdminShop | null>(null);
  const [newStatus, setNewStatus] = useState('active');
  const [confirmStatus, setConfirmStatus] = useState<AdminShop | null>(null);

  const [renewTarget, setRenewTarget] = useState<AdminShop | null>(null);
  const [renewDays, setRenewDays] = useState('365');
  const [renewChannel, setRenewChannel] = useState('offline_cash');
  const [renewAmount, setRenewAmount] = useState('');
  const [renewRemark, setRenewRemark] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'shops', status, search],
    queryFn: () =>
      listAdminShops({
        status: status || undefined,
        search: search || undefined,
        pageSize: 50,
      }),
  });

  const statusMutation = useMutation({
    mutationFn: () => updateShopStatus(statusTarget!.shop.id, newStatus as 'trial' | 'active' | 'readonly' | 'expired'),
    onSuccess: () => {
      setStatusTarget(null);
      setConfirmStatus(null);
      setError('');
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '状态更新失败'),
  });

  const renewMutation = useMutation({
    mutationFn: () =>
      manualRenewShop(renewTarget!.shop.id, {
        days: parseInt(renewDays, 10) || 365,
        channel: renewChannel,
        amountCents: renewAmount ? Math.round(parseFloat(renewAmount) * 100) : undefined,
        remark: renewRemark.trim() || undefined,
      }),
    onSuccess: () => {
      setRenewTarget(null);
      setRenewDays('365');
      setRenewAmount('');
      setRenewRemark('');
      setError('');
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '续费失败'),
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1E293B]">租户门店</h1>
        <p className="mt-1 text-sm text-[#64748B]">
          管理全部租户门店，查看门店状态与订阅信息
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]">{error}</div>
      )}

      {/* 筛选 */}
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索门店名称..."
              className="pl-9"
            />
          </div>
          <div className="flex gap-2">
            {['', 'trial', 'active', 'readonly', 'expired'].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                  status === s
                    ? 'bg-[#2563EB] text-white'
                    : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
                }`}
              >
                {s === '' ? '全部' : statusLabels[s]}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 门店列表 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            门店列表
            <span className="text-sm font-normal text-[#64748B]">
              共 {data?.total ?? 0} 家
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-sm text-[#94A3B8]">加载中...</div>
          ) : isError ? (
            <div className="py-12 text-center text-sm text-[#DC2626]">加载失败，请刷新重试</div>
          ) : !data?.rows.length ? (
            <div className="py-12 text-center">
              <Building2 className="mx-auto h-12 w-12 text-[#E2E8F0]" />
              <p className="mt-3 text-sm text-[#94A3B8]">
                {search ? '未找到匹配的门店' : '暂无租户门店'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left text-sm text-[#64748B]">
                    <th className="pb-3 font-medium">门店名称</th>
                    <th className="pb-3 font-medium">店主</th>
                    <th className="pb-3 font-medium">员工数</th>
                    <th className="pb-3 font-medium">状态</th>
                    <th className="pb-3 font-medium">套餐</th>
                    <th className="pb-3 font-medium">到期时间</th>
                    <th className="pb-3 font-medium text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {data.rows.map((item) => {
                    const { shop, owner, subscription, plan, memberCount } = item;
                    return (
                      <tr key={shop.id} className="text-sm">
                        <td className="py-3">
                          <div className="font-medium text-[#1E293B]">{shop.name}</div>
                          <div className="mt-0.5 text-xs text-[#94A3B8]">
                            创建于 {new Date(shop.createdAt).toLocaleDateString('zh-CN')}
                          </div>
                        </td>
                        <td className="py-3">
                          {owner ? (
                            <div>
                              <div className="flex items-center gap-1 text-[#64748B]">
                                <Phone className="h-3.5 w-3.5" />
                                {owner.phone}
                              </div>
                              <div className="mt-0.5 text-xs text-[#94A3B8]">{owner.displayName || '未设置'}</div>
                            </div>
                          ) : (
                            <span className="text-[#94A3B8]">-</span>
                          )}
                        </td>
                        <td className="py-3">
                          <span className="flex items-center gap-1 text-[#64748B]">
                            <Users className="h-4 w-4" />
                            {memberCount} 人
                          </span>
                        </td>
                        <td className="py-3">
                          <Badge variant={statusVariants[shop.status] || 'default'}>
                            {statusLabels[shop.status] || shop.status}
                          </Badge>
                        </td>
                        <td className="py-3 text-[#64748B]">{plan?.name || '-'}</td>
                        <td className="py-3 text-[#64748B]">
                          {subscription?.endAt
                            ? new Date(subscription.endAt).toLocaleDateString('zh-CN')
                            : '-'}
                        </td>
                        <td className="py-3">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => {
                                setRenewTarget(item);
                                setError('');
                              }}
                              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-[#2563EB] hover:bg-[#EFF6FF]"
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                              续费
                            </button>
                            <button
                              onClick={() => {
                                setStatusTarget(item);
                                setNewStatus(shop.status);
                                setError('');
                              }}
                              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-[#64748B] hover:bg-[#F1F5F9]"
                            >
                              <Settings2 className="h-3.5 w-3.5" />
                              改状态
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 状态变更弹窗 */}
      <Modal
        open={!!statusTarget}
        onClose={() => setStatusTarget(null)}
        title={`变更门店状态 — ${statusTarget?.shop.name ?? ''}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setStatusTarget(null)} disabled={statusMutation.isPending}>
              取消
            </Button>
            <Button
              onClick={() => {
                if (newStatus === 'expired' || newStatus === 'readonly') {
                  setConfirmStatus(statusTarget);
                } else {
                  statusMutation.mutate();
                }
              }}
              disabled={statusMutation.isPending || newStatus === statusTarget?.shop.status}
            >
              {statusMutation.isPending ? '处理中...' : '确认变更'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-lg bg-[#F8FAFC] p-3 text-sm text-[#64748B]">
            当前状态：
            <Badge variant={statusVariants[statusTarget?.shop.status ?? ''] || 'default'}>
              {statusLabels[statusTarget?.shop.status ?? ''] || statusTarget?.shop.status}
            </Badge>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">新状态</label>
            <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              <option value="trial">试用中</option>
              <option value="active">正式使用</option>
              <option value="readonly">只读期（限制写入）</option>
              <option value="expired">已过期（停用）</option>
            </Select>
          </div>
          <p className="text-xs text-[#94A3B8]">
            「只读期」和「已过期」会限制门店正常使用，变更前会再次确认。操作将记录审计日志。
          </p>
        </div>
      </Modal>

      {/* 停用确认 */}
      <ConfirmModal
        open={!!confirmStatus}
        onClose={() => setConfirmStatus(null)}
        onConfirm={() => statusMutation.mutate()}
        title="确认停用门店？"
        description={`将「${confirmStatus?.shop.name}」状态变更为「${statusLabels[newStatus]}」。该门店员工将无法正常开单，请确认已与店主沟通。`}
        confirmText="确认变更"
        loading={statusMutation.isPending}
      />

      {/* 手动续费弹窗 */}
      <Modal
        open={!!renewTarget}
        onClose={() => setRenewTarget(null)}
        title={`手动续费 — ${renewTarget?.shop.name ?? ''}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setRenewTarget(null)} disabled={renewMutation.isPending}>
              取消
            </Button>
            <Button onClick={() => renewMutation.mutate()} disabled={renewMutation.isPending}>
              {renewMutation.isPending ? '处理中...' : '确认续费'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-lg bg-[#F8FAFC] p-3 text-sm text-[#64748B]">
            {renewTarget?.subscription?.endAt ? (
              <>
                当前到期：<span className="font-medium text-[#1E293B]">
                  {new Date(renewTarget.subscription.endAt).toLocaleDateString('zh-CN')}
                </span>
                <br />
              </>
            ) : (
              <>该门店暂无订阅记录，将创建/延长订阅。<br /></>
            )}
            将生成一条线下收款的平台支付订单并自动确认。
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-[#334155]">续费天数 *</label>
              <Select value={renewDays} onChange={(e) => setRenewDays(e.target.value)}>
                <option value="30">30 天</option>
                <option value="90">90 天</option>
                <option value="180">180 天</option>
                <option value="365">365 天（一年）</option>
                <option value="730">730 天（两年）</option>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[#334155]">收款方式</label>
              <Select value={renewChannel} onChange={(e) => setRenewChannel(e.target.value)}>
                <option value="offline_cash">线下现金</option>
                <option value="offline_wechat">线下微信</option>
                <option value="offline_bank">线下转账</option>
              </Select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">
              实收金额（元，留空按套餐年价）
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={renewAmount}
              onChange={(e) => setRenewAmount(e.target.value)}
              placeholder="如：3999"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">备注</label>
            <Input
              value={renewRemark}
              onChange={(e) => setRenewRemark(e.target.value)}
              placeholder="可选，如：地推优惠"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
